import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Panic / SOS — canonical member-facing routes:
 *   - contacts:        `/public/me/panic-contacts*`
 *   - emergency types: `GET /panic/emergency-types/active`
 *   - trigger:         `POST /panic/trigger`
 *   - my history:      `GET /public/me/panic-logs`
 *   - cancel:          `POST /public/me/panic-logs/:logId/cancel`
 */

export type PanicContactSource = 'manual' | 'core_user';

/** Normalised contact for UI — maps tenant API `memberpaniccontact` rows. */
export interface PanicContact {
  contact_id: string;
  contact_source: PanicContactSource;
  name: string;
  mobile: string;
  email?: string;
  notify_via_sms: boolean;
  notify_via_email: boolean;
  notify_via_push: boolean;
  is_active: boolean;
  core_user_id?: string | null;
  same_community_member_id?: string | null;
  same_community_user_id?: string | null;
  sort_order?: number;
}

export interface PanicContactInput {
  contact_id?: string;
  contact_source?: PanicContactSource;
  core_user_id?: string | null;
  name: string;
  mobile: string;
  email?: string;
  notify_via_sms?: boolean;
  notify_via_email?: boolean;
  notify_via_push?: boolean;
  is_active?: boolean;
}

export interface PanicContactSearchResult {
  core_user_id: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  profile_image_url: string | null;
  is_same_community: boolean;
  same_community_member_id: string | null;
  same_community_user_id: string | null;
  phone: string | null;
  push_allowed: boolean;
}

export interface EmergencyType {
  /** The API field is `panic_emergency_type_id` — normalised here. */
  emergency_type_id: string;
  name: string;
  bg_color?: string;
  notification_level?: 'individual' | 'responder_group' | 'national';
}

export interface PanicTriggerPayload {
  emergency_type_id: string;
  latitude?: number;
  longitude?: number;
  metadata?: Record<string, unknown>;
}

export interface PanicTriggerResult {
  panic_log_id: string;
  status: PanicStatus;
}

export type PanicStatus =
  | 'triggered'
  | 'dispatched'
  | 'acknowledged'
  | 'in_progress'
  | 'escalated'
  | 'resolved'
  | 'cancelled';

export type PanicLogsBucket = 'active' | 'resolved' | 'cancelled' | 'all';

/** Statuses where the alert is still live and can be cancelled. */
const ACTIVE_PANIC_STATUSES: PanicStatus[] = [
  'triggered',
  'dispatched',
  'acknowledged',
  'in_progress',
  'escalated',
];

export function isActivePanicStatus(status: string): boolean {
  return (ACTIVE_PANIC_STATUSES as string[]).includes(status);
}

export interface PanicLog {
  panic_log_id: string;
  emergency_type_id: string;
  emergency_type_name: string;
  status: PanicStatus;
  /** Stored as text in the DB — parse before use. */
  latitude?: string | number | null;
  longitude?: string | number | null;
  created_at: string;
  resolved_at?: string | null;
  closed_at?: string | null;
}

interface FetchMyPanicLogsOptions {
  bucket?: PanicLogsBucket;
  limit?: number;
  page?: number;
}

type MemberPanicContactRow = {
  member_panic_contact_id: string;
  contact_source?: PanicContactSource;
  core_user_id?: string | null;
  same_community_member_id?: string | null;
  same_community_user_id?: string | null;
  contact_name?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  notify_via_sms?: boolean;
  notify_via_email?: boolean;
  notify_via_push?: boolean;
  is_active?: boolean;
  sort_order?: number;
};

function mapPanicContactRow(row: MemberPanicContactRow): PanicContact {
  return {
    contact_id: row.member_panic_contact_id,
    contact_source: row.contact_source ?? 'manual',
    name: row.contact_name ?? '',
    mobile: row.contact_phone ?? '',
    email: row.contact_email ?? undefined,
    notify_via_sms: Boolean(row.notify_via_sms),
    notify_via_email: Boolean(row.notify_via_email),
    notify_via_push: Boolean(row.notify_via_push),
    is_active: row.is_active ?? true,
    core_user_id: row.core_user_id ?? null,
    same_community_member_id: row.same_community_member_id ?? null,
    same_community_user_id: row.same_community_user_id ?? null,
    sort_order: row.sort_order ?? 0,
  };
}

function toApiPayload(input: PanicContactInput): Record<string, unknown> {
  const phone = input.mobile.trim();
  const email = input.email?.trim() || null;
  const notifyViaSms = input.notify_via_sms ?? Boolean(phone);
  const notifyViaEmail = input.notify_via_email ?? Boolean(email);

  return {
    contact_source: input.contact_source ?? 'manual',
    core_user_id: input.core_user_id ?? null,
    contact_name: input.name.trim(),
    contact_phone: phone || null,
    contact_email: email,
    notify_via_sms: notifyViaSms,
    notify_via_email: notifyViaEmail,
    notify_via_push: input.notify_via_push ?? false,
    is_active: input.is_active ?? true,
  };
}

export async function fetchPanicContacts(): Promise<PanicContact[]> {
  const { data } = await tenantApi.get('/public/me/panic-contacts');
  return unwrapList<MemberPanicContactRow>(data).map(mapPanicContactRow);
}

export async function fetchEmergencyTypes(): Promise<EmergencyType[]> {
  const { data } = await tenantApi.get('/panic/emergency-types/active');
  return unwrapList<{
    panic_emergency_type_id: string;
    name: string;
    bg_color?: string;
    notification_level?: EmergencyType['notification_level'];
  }>(data).map((type) => ({
    emergency_type_id: type.panic_emergency_type_id,
    name: type.name,
    bg_color: type.bg_color,
    notification_level: type.notification_level,
  }));
}

export async function upsertPanicContact(input: PanicContactInput): Promise<PanicContact> {
  const payload = toApiPayload(input);
  if (input.contact_id) {
    const { data } = await tenantApi.put(`/public/me/panic-contacts/${input.contact_id}`, payload);
    return mapPanicContactRow(unwrap<MemberPanicContactRow>(data));
  }
  const { data } = await tenantApi.post('/public/me/panic-contacts', payload);
  return mapPanicContactRow(unwrap<MemberPanicContactRow>(data));
}

export async function deletePanicContact(contactId: string): Promise<void> {
  await tenantApi.delete(`/public/me/panic-contacts/${contactId}`);
}

export async function searchPanicContacts(
  query: string,
  limit = 10,
): Promise<PanicContactSearchResult[]> {
  const { data } = await tenantApi.get('/public/me/panic-contact-search', {
    params: {
      q: query,
      limit,
    },
  });
  return unwrapList<PanicContactSearchResult>(data);
}

export async function triggerPanic(payload: PanicTriggerPayload): Promise<PanicTriggerResult> {
  const { data } = await tenantApi.post('/panic/trigger', payload);
  const result = unwrap<{ panic_log_id: string; status?: string }>(data);
  return {
    panic_log_id: result.panic_log_id,
    status: (result.status as PanicStatus) ?? 'triggered',
  };
}

export async function fetchMyPanicLogs(options: FetchMyPanicLogsOptions = {}): Promise<PanicLog[]> {
  const { data } = await tenantApi.get('/public/me/panic-logs', { params: options });
  return unwrapList<PanicLog>(data);
}

export async function cancelPanic(logId: string, note?: string): Promise<void> {
  await tenantApi.post(`/public/me/panic-logs/${logId}/cancel`, {
    note: note ?? 'Cancelled by resident from the app',
  });
}
